/**
 * Toasts, hand-rolled on a live region.
 *
 * Not Radix here: the whole surface is "push a message, it leaves after a few
 * seconds", and the accessibility contract is a single `aria-live` region that
 * exists from first paint. A toast added to a region that appears at the same
 * time is often not announced at all, which is why `<ToastViewport/>` is mounted
 * unconditionally in `App`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Toast.module.css';

export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

type Listener = (toasts: Toast[]) => void;

let items: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener(items);
}

function dismiss(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(tone: ToastTone, message: string, ttlMs = 5000): number {
  const id = nextId++;
  items = [...items, { id, tone, message }];
  emit();
  setTimeout(() => {
    dismiss(id);
  }, ttlMs);
  return id;
}

/** Callable from anywhere, React or not — the REST client uses it on failures. */
export const toast = {
  info: (message: string) => push('info', message),
  success: (message: string) => push('success', message),
  error: (message: string) => push('error', message, 7000),
  dismiss,
  clear: () => {
    items = [];
    emit();
  },
};

export function ToastViewport() {
  const { t } = useTranslation('common');
  const [current, setCurrent] = useState<Toast[]>(items);

  useEffect(() => {
    listeners.add(setCurrent);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);

  return (
    <div className={styles.viewport} role="region" aria-label={t('notifications')}>
      {current.map((item) => (
        <div
          key={item.id}
          className={[styles.toast, styles[item.tone]].join(' ')}
          // Errors interrupt; everything else waits for a pause in speech.
          role={item.tone === 'error' ? 'alert' : 'status'}
          aria-live={item.tone === 'error' ? 'assertive' : 'polite'}
          data-testid={`toast-${item.tone}`}
        >
          <span>{item.message}</span>
          <button
            type="button"
            className={styles.close}
            onClick={() => {
              dismiss(item.id);
            }}
            aria-label={t('dismiss')}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
